import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ECONOMY_MODE_CONFIG } from '@/lib/performanceConfig';

interface EconomyModeState {
  isEconomyMode: boolean;
  activatedAt: Date | null;
  failCount: number;
}

interface EconomyModeContextType extends EconomyModeState {
  reportQueryFailure: (error: unknown) => void;
  reportQuerySuccess: () => void;
  forceRefresh: () => void;
}

const EconomyModeContext = createContext<EconomyModeContextType | null>(null);

/**
 * Classifica se o erro é transitório (stress do banco) ou permanente (permissão).
 * Apenas erros transitórios incrementam o contador de falhas.
 */
function isTransientError(error: unknown): boolean {
  if (!error) return false;

  const errorStr = String(error).toLowerCase();
  const message = (error as any)?.message?.toLowerCase() || '';
  const code = (error as any)?.code || '';
  const status = (error as any)?.status || (error as any)?.statusCode || 0;

  // Erros de permissão/autenticação → NÃO são transitórios
  if (status === 401 || status === 403 || status === 404) return false;
  if (code === '42501' || code === '42P01') return false; // permission denied, table not found

  // Erros transitórios (stress do banco)
  if (status === 429 || status >= 500) return true;
  if (code === '57014') return true; // statement_timeout
  if (errorStr.includes('timeout') || message.includes('timeout')) return true;
  if (errorStr.includes('too many') || message.includes('too many')) return true;
  if (errorStr.includes('network') || message.includes('fetch')) return true;
  if (errorStr.includes('aborted') || message.includes('aborted')) return true;

  // Default: considerar transitório para ser conservador
  return true;
}

export function EconomyModeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<EconomyModeState>({
    isEconomyMode: false,
    activatedAt: null,
    failCount: 0,
  });

  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Limpar timer ao desmontar
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  const activateEconomyMode = useCallback(() => {
    console.warn('⚡ [EconomyMode] ATIVADO - pausando queries pesadas por', ECONOMY_MODE_CONFIG.COOLDOWN_MS / 60000, 'min');
    
    setState(prev => ({
      ...prev,
      isEconomyMode: true,
      activatedAt: new Date(),
    }));

    // Auto-desativar após cooldown
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => {
      console.info('✅ [EconomyMode] DESATIVADO - retomando operação normal');
      setState({ isEconomyMode: false, activatedAt: null, failCount: 0 });
    }, ECONOMY_MODE_CONFIG.COOLDOWN_MS);
  }, []);

  const reportQueryFailure = useCallback((error: unknown) => {
    if (!isTransientError(error)) {
      console.log('🔒 [EconomyMode] Erro de permissão ignorado (não é stress):', error);
      return;
    }

    setState(prev => {
      const newCount = prev.failCount + 1;
      console.warn(`⚠️ [EconomyMode] Falha transitória ${newCount}/${ECONOMY_MODE_CONFIG.FAILURE_THRESHOLD}`);
      
      if (newCount >= ECONOMY_MODE_CONFIG.FAILURE_THRESHOLD && !prev.isEconomyMode) {
        // Ativar no próximo tick para evitar setState durante render
        setTimeout(() => activateEconomyMode(), 0);
      }
      
      return { ...prev, failCount: newCount };
    });
  }, [activateEconomyMode]);

  const reportQuerySuccess = useCallback(() => {
    setState(prev => {
      if (prev.failCount === 0) return prev;
      return { ...prev, failCount: Math.max(0, prev.failCount - 1) };
    });
  }, []);

  const forceRefresh = useCallback(() => {
    console.info('🔄 [EconomyMode] Refresh manual solicitado');
    setState({ isEconomyMode: false, activatedAt: null, failCount: 0 });
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
  }, []);

  const value: EconomyModeContextType = useMemo(() => ({
    ...state,
    reportQueryFailure,
    reportQuerySuccess,
    forceRefresh,
  }), [state, reportQueryFailure, reportQuerySuccess, forceRefresh]);

  return (
    <EconomyModeContext.Provider value={value}>
      {children}
    </EconomyModeContext.Provider>
  );
}

export function useEconomyMode() {
  const ctx = useContext(EconomyModeContext);
  if (!ctx) throw new Error('useEconomyMode deve ser usado dentro de EconomyModeProvider');
  return ctx;
}
